import {
	AlertTriangle,
	ChevronRight,
	Eye,
	FilePlus2,
	Hand,
	Timer,
	X,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { fetchJson, sendJson } from "../../api/rest";
import { useCatalogue } from "../../components/flows/catalogue";
import { HelpPopover } from "../../components/flows/NodeHelp";
import {
	accentOf,
	btn,
	describeCron,
	ErrorBanner,
	NodeIcon,
	Spinner,
} from "../../components/flows/ui";
import type {
	ChannelInfo,
	TemplateEntry,
	TemplateGallery,
} from "../../types/flows";

// Choosing a template is a browsing task, so the gallery is built like one: pick
// a channel on the left, scan cards on the right. The full node chain — which is
// what made the cards tall enough to hide everything below the first row — moved
// into the preview, where it can be read properly instead of squinted at.

const ALL = "all";

/** The chain as a miniature canvas, so a card reads as "a flow" at a glance. */
const ChainPreview = ({ chain }: { chain: TemplateEntry["chain"] }) => {
	const { node, accentFor } = useCatalogue();
	const shown = chain.slice(0, 8);
	const hidden = chain.length - shown.length;

	return (
		<div
			// Wraps rather than scrolls: a card is narrower than a nine-node chain at every
			// breakpoint, and a row clipped mid-icon reads as a rendering fault.
			className="flex h-24 flex-wrap content-center items-center justify-center gap-y-1.5 overflow-hidden rounded-xl border border-slate-200 bg-slate-50/70 px-2"
			// The same dot grid the editor canvas uses, so the two read as one surface.
			style={{
				backgroundImage: "radial-gradient(#e2e8f0 1px, transparent 1px)",
				backgroundSize: "10px 10px",
			}}
		>
			{shown.map((step, index) => {
				const def = node(step.type);
				const tone = accentOf(accentFor(step.type));
				return (
					<Fragment key={step.nodeId}>
						{index > 0 && <span className="h-px w-2 shrink-0 bg-slate-300" />}
						<span
							title={`${step.label}${def ? ` — ${def.help.summary}` : ""}`}
							className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md border bg-white shadow-sm ${tone.border} ${tone.text}`}
						>
							<NodeIcon name={def?.icon ?? "Circle"} className="h-3 w-3" />
						</span>
					</Fragment>
				);
			})}
			{hidden > 0 && (
				<>
					<span className="h-px w-2 shrink-0 bg-slate-300" />
					<span className="flex h-6 shrink-0 items-center rounded-md border border-slate-200 bg-white px-1.5 text-[10px] font-medium text-slate-500 shadow-sm">
						+{hidden}
					</span>
				</>
			)}
		</div>
	);
};

/** The labelled chain, with the same help popovers the canvas and palette use. */
const ChainDetail = ({ chain }: { chain: TemplateEntry["chain"] }) => {
	const { node, accentFor } = useCatalogue();
	return (
		<div className="flex flex-wrap items-center gap-1">
			{chain.map((step, index) => {
				const def = node(step.type);
				const tone = accentOf(accentFor(step.type));
				return (
					<span key={step.nodeId} className="flex items-center gap-1">
						{index > 0 && (
							<ChevronRight className="h-3 w-3 shrink-0 text-slate-300" />
						)}
						<span
							className={`flex items-center gap-1 rounded-lg border px-1.5 py-1 text-[11px] ${tone.border} ${tone.bg} ${tone.text}`}
						>
							<NodeIcon
								name={def?.icon ?? "Circle"}
								className="h-3 w-3 shrink-0"
							/>
							<span className="max-w-40 truncate">{step.label}</span>
							{def && <HelpPopover def={def} accent={accentFor(step.type)} />}
						</span>
					</span>
				);
			})}
		</div>
	);
};

const TriggerNote = ({ template }: { template: TemplateEntry }) => {
	if (template.humanTriggerOnly) {
		return (
			<span
				className="flex items-center gap-1 text-[11px] text-violet-700"
				title="Comparisons are started by a person choosing candidates, so this flow cannot be scheduled."
			>
				<Hand className="h-3 w-3 shrink-0" /> Started by a person
			</span>
		);
	}
	if (template.defaultSchedule) {
		return (
			<span
				className="flex items-center gap-1 text-[11px] text-slate-500"
				title="A suggestion. Instantiating a template never creates a live schedule."
			>
				<Timer className="h-3 w-3 shrink-0" />
				{template.defaultSchedule.name ??
					describeCron(template.defaultSchedule.cronExpression)}
			</span>
		);
	}
	return <span className="text-[11px] text-slate-400">No schedule</span>;
};

const MissingNote = ({ keys }: { keys: string[] }) =>
	keys.length === 0 ? null : (
		<span
			className="flex items-center gap-1 text-[11px] font-medium text-warning-dark"
			title={`Not configured yet: ${keys.join(", ")}`}
		>
			<AlertTriangle className="h-3 w-3 shrink-0" />
			Needs setup
		</span>
	);

const TemplateCard = ({
	template,
	onUse,
	onPreview,
	busy,
}: {
	template: TemplateEntry;
	onUse: (template: TemplateEntry) => void;
	onPreview: (template: TemplateEntry) => void;
	busy: boolean;
}) => (
	<div className="group flex flex-col rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
		<div className="relative">
			<ChainPreview chain={template.chain} />
			<button
				type="button"
				onClick={() => onPreview(template)}
				aria-label={`Preview ${template.name}`}
				title="See every step"
				className="absolute right-2 top-2 rounded-full border border-slate-200 bg-white/90 p-1.5 text-slate-500 opacity-0 shadow-sm transition-opacity hover:text-slate-700 focus:opacity-100 group-hover:opacity-100"
			>
				<Eye className="h-3.5 w-3.5" />
			</button>
		</div>

		<div className="flex flex-1 flex-col px-1 pt-3">
			<h4 className="text-[14px] font-semibold text-slate-800">
				{template.name}
			</h4>
			{/* Two lines, so every card in a row is the same height whatever the copy. */}
			<p className="mt-1 line-clamp-2 text-[12px] leading-relaxed text-slate-500">
				{template.description}
			</p>

			<div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1">
				<TriggerNote template={template} />
				<MissingNote keys={template.missingSettings} />
			</div>

			<button
				type="button"
				disabled={busy}
				onClick={() => onUse(template)}
				className={`${btn.primary} mt-3 w-full justify-center`}
			>
				Use this template
			</button>
		</div>
	</div>
);

const PreviewDialog = ({
	template,
	channel,
	onClose,
	onUse,
	busy,
}: {
	template: TemplateEntry;
	channel: ChannelInfo | undefined;
	onClose: () => void;
	onUse: (template: TemplateEntry) => void;
	busy: boolean;
}) => {
	const tone = accentOf(channel?.accent);
	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
			<div
				className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl"
				role="dialog"
				aria-modal="true"
				aria-label={template.name}
			>
				<div className="flex items-start gap-3">
					<div className="flex-1">
						{channel && (
							<span
								className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.bg} ${tone.text}`}
							>
								{channel.label}
							</span>
						)}
						<h3 className="mt-2 text-[17px] font-semibold text-slate-800">
							{template.name}
						</h3>
						<p className="mt-1 text-[13px] leading-relaxed text-slate-500">
							{template.description}
						</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="mt-5">
					<p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
						Every step
					</p>
					<ChainDetail chain={template.chain} />
				</div>

				{channel && (
					<div
						className={`mt-5 rounded-xl border p-3 ${tone.border} ${tone.bg}`}
					>
						<p className={`text-[12px] font-medium ${tone.text}`}>
							What every {channel.label} flow does
						</p>
						<ul className="mt-1.5 space-y-0.5">
							{channel.defaults.map((rule) => (
								<li
									key={rule}
									className="flex items-start gap-1.5 text-[11px] text-slate-600"
								>
									<span
										className={`mt-1.5 h-1 w-1 shrink-0 rounded-full ${tone.dot}`}
									/>
									{rule}
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
					<TriggerNote template={template} />
				</div>

				{template.missingSettings.length > 0 && (
					<div className="mt-4 rounded-xl border border-warning/30 bg-warning/5 p-3 text-[12px] text-warning-dark">
						<p className="flex items-center gap-1.5 font-medium">
							<AlertTriangle className="h-3.5 w-3.5" />
							Needs configuring before it can run
						</p>
						<p className="mt-1 font-mono text-[11px] text-slate-500">
							{template.missingSettings.join(", ")}
						</p>
						<Link
							to="/flows/settings"
							className="mt-1.5 inline-block font-medium text-primary underline"
						>
							Open Settings
						</Link>
					</div>
				)}

				<div className="mt-6 flex justify-end gap-2">
					<button type="button" onClick={onClose} className={btn.ghost}>
						Close
					</button>
					<button
						type="button"
						disabled={busy}
						onClick={() => onUse(template)}
						className={btn.primary}
					>
						Use this template
					</button>
				</div>
			</div>
		</div>
	);
};

const CategoryRow = ({
	label,
	count,
	active,
	accent,
	onClick,
}: {
	label: string;
	count: number;
	active: boolean;
	accent?: string;
	onClick: () => void;
}) => {
	const tone = accentOf(accent);
	return (
		<button
			type="button"
			onClick={onClick}
			className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[13px] transition-colors ${
				active
					? "bg-primary/8 font-semibold text-primary"
					: "text-slate-600 hover:bg-slate-50"
			}`}
		>
			{accent && (
				<span className={`h-2 w-2 shrink-0 rounded-full ${tone.dot}`} />
			)}
			<span className="flex-1 truncate">{label}</span>
			<span
				className={`rounded-full px-1.5 py-0.5 text-[11px] font-medium ${
					active ? "bg-primary text-white" : "bg-slate-100 text-slate-500"
				}`}
			>
				{count}
			</span>
		</button>
	);
};

const FlowsTemplates = () => {
	const navigate = useNavigate();
	const [gallery, setGallery] = useState<TemplateGallery | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [channelId, setChannelId] = useState<string>(ALL);
	const [previewing, setPreviewing] = useState<TemplateEntry | null>(null);

	useEffect(() => {
		fetchJson<TemplateGallery>("/flows/templates")
			.then(setGallery)
			.catch((e) => setError(String(e)));
	}, []);

	const countOf = useCallback(
		(id: string) =>
			id === ALL
				? (gallery?.templates.length ?? 0)
				: (gallery?.templates.filter((t) => t.channel === id).length ?? 0),
		[gallery],
	);

	const visible = useMemo(() => {
		if (!gallery) return [];
		return channelId === ALL
			? gallery.templates
			: gallery.templates.filter((t) => t.channel === channelId);
	}, [gallery, channelId]);

	const selected = gallery?.channels.find((c) => c.id === channelId);

	const use = useCallback(
		async (template: TemplateEntry) => {
			setBusy(true);
			setError(null);
			try {
				const created = await sendJson<{ id: number; error?: string }>(
					`/flows/templates/${template.id}/instantiate`,
					"POST",
					{},
				);
				if (created.id) navigate(`/flows/${created.id}`);
				else setError(created.error ?? "The template could not be copied.");
			} catch (e) {
				setError(String(e));
			} finally {
				setBusy(false);
			}
		},
		[navigate],
	);

	const startBlank = useCallback(async () => {
		setBusy(true);
		setError(null);
		try {
			const created = await sendJson<{ id: number; error?: string }>(
				"/flows",
				"POST",
				{ name: "Untitled flow" },
			);
			if (created.id) navigate(`/flows/${created.id}`);
			else setError(created.error ?? "The flow could not be created.");
		} catch (e) {
			setError(String(e));
		} finally {
			setBusy(false);
		}
	}, [navigate]);

	if (!gallery && !error) return <Spinner />;

	return (
		<div className="space-y-4">
			<ErrorBanner error={error} />

			<div className="flex flex-col gap-5 lg:flex-row">
				<aside className="lg:w-56 lg:shrink-0">
					<div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:sticky lg:top-20">
						<p className="px-3 pb-2 pt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
							Channels
						</p>
						<div className="space-y-0.5">
							<CategoryRow
								label="Show all"
								count={countOf(ALL)}
								active={channelId === ALL}
								onClick={() => setChannelId(ALL)}
							/>
							{gallery?.channels.map((channel) => (
								<CategoryRow
									key={channel.id}
									label={channel.label}
									count={countOf(channel.id)}
									accent={channel.accent}
									active={channelId === channel.id}
									onClick={() => setChannelId(channel.id)}
								/>
							))}
						</div>

						<div className="mt-3 border-t border-slate-100 pt-3">
							<button
								type="button"
								disabled={busy}
								onClick={startBlank}
								className={`${btn.ghost} w-full justify-center`}
							>
								<FilePlus2 className="h-4 w-4" />
								Start from blank
							</button>
						</div>
					</div>
				</aside>

				<div className="min-w-0 flex-1 space-y-4">
					{/* One line of context instead of a banner per channel; the rules that used
					    to sit here are in each template's preview, where they apply. */}
					<div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
						<h3 className="text-[15px] font-semibold text-slate-800">
							{selected ? selected.label : "All templates"}
						</h3>
						<p className="text-[12px] text-slate-500">
							{selected
								? selected.description
								: "Every template arrives as a draft. Nothing runs until you publish it."}
						</p>
					</div>

					<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
						{visible.map((template) => (
							<TemplateCard
								key={template.id}
								template={template}
								onUse={use}
								onPreview={setPreviewing}
								busy={busy}
							/>
						))}
					</div>
				</div>
			</div>

			{previewing && (
				<PreviewDialog
					template={previewing}
					channel={gallery?.channels.find((c) => c.id === previewing.channel)}
					onClose={() => setPreviewing(null)}
					onUse={use}
					busy={busy}
				/>
			)}
		</div>
	);
};

export default FlowsTemplates;
